<template>
  <TransitionRoot
    as="template"
    :show="agentStore.isOpen"
    enter="transform transition ease-out duration-200"
    enter-from="opacity-0 translate-y-4"
    enter-to="opacity-100 translate-y-0"
    leave="transform transition ease-in duration-150"
    leave-from="opacity-100 translate-y-0"
    leave-to="opacity-0 translate-y-4"
  >
    <div
      class="fixed z-50 inset-0 sm:inset-auto sm:bottom-24 sm:right-4 flex flex-col bg-white sm:rounded-lg shadow-xl ring-1 ring-black ring-opacity-5 overflow-hidden transition-all duration-200"
      :class="isExpanded ? 'sm:w-[min(52rem,calc(100vw-2rem))] sm:h-[min(52rem,calc(100vh-8rem))]' : 'sm:w-96 sm:h-[600px]'"
    >
      <div class="flex items-center justify-between px-4 py-3 bg-blue-primary text-white">
        <img src="/sammi/logo-white.png" alt="SAMMI — Salvation Army Mission Manager Intelligence" class="w-auto h-auto max-h-10 max-w-[240px] object-contain" />
        <div class="flex items-center gap-3">
          <button
            type="button"
            class="hidden sm:block hover:text-gray-5"
            :aria-label="isExpanded ? 'Shrink window' : 'Expand window'"
            :title="isExpanded ? 'Shrink window' : 'Expand window'"
            @click="onToggleExpand"
          >
            <Icon :name="isExpanded ? 'heroicons:arrows-pointing-in' : 'heroicons:arrows-pointing-out'" class="w-5 h-5" />
          </button>
          <button
            type="button"
            class="hover:text-gray-5"
            aria-label="New conversation"
            title="New conversation"
            @click="onReset"
          >
            <Icon name="heroicons:arrow-path" class="w-5 h-5" />
          </button>
          <button
            type="button"
            class="hover:text-gray-5"
            aria-label="Minimize"
            title="Minimize"
            @click="agentStore.isOpen = false"
          >
            <Icon name="heroicons:chevron-down" class="w-6 h-6" />
          </button>
        </div>
      </div>

      <AgentMessageList class="flex-1" />

      <div class="border-t border-gray-300 p-3">
        <div class="flex items-end gap-2">
          <div class="flex-1">
            <FormKit
              type="textarea"
              name="agentMessage"
              v-model="input"
              rows="2"
              placeholder="Ask SAMMI about applicants, assistance requests, appointments, funds..."
              input-class="$reset w-full rounded-md border border-gray-400 font-proxima text-sm p-2 resize-none"
              outer-class="!mb-0"
              @keydown.enter.exact.prevent="onSend"
            />
          </div>
          <button
            type="button"
            class="rounded-md text-sm font-proxima border border-black px-3 py-2 hover:bg-gray-100 disabled:bg-gray-300 disabled:cursor-not-allowed"
            :disabled="!canSend"
            @click="onSend"
          >
            <span v-if="isStreaming">Sending...</span>
            <span v-else class="flex items-center gap-1">
              <span class="hidden md:block">Send</span>
              <Icon name="teenyicons:send-solid" class="block md:hidden w-4 h-4" />
            </span>
          </button>
        </div>
      </div>
    </div>
  </TransitionRoot>
</template>
<script setup lang="ts">
import { TransitionRoot } from '@headlessui/vue';

const agentStore = useAgentStore();

const input = ref('');

// Window size: manual toggle wins; otherwise auto-expand the first time an
// assistant reply contains a markdown table (wide content).
const isExpanded = ref(false);
const userSized = ref(false);
const onToggleExpand = () => {
  isExpanded.value = !isExpanded.value;
  userSized.value = true;
};
const hasTable = computed(() =>
  (agentStore.messages ?? []).some(
    (m: any) =>
      m.role === 'assistant' &&
      m.parts?.some((p: any) => p.type === 'text' && /\n\|.+\|\s*\n\|[\s:|-]+\|/.test(p.text ?? '')),
  ),
);
watch(hasTable, (wide) => {
  if (wide && !userSized.value) isExpanded.value = true;
});
watch(
  () => agentStore.messages?.length ?? 0,
  (len) => {
    // New conversation resets sizing behavior
    if (len === 0) {
      isExpanded.value = false;
      userSized.value = false;
    }
  },
);

const isStreaming = computed(() => agentStore.status === 'submitted' || agentStore.status === 'streaming');
// A pending confirmation also blocks sending: the last assistant message has an
// unresolved tool call, so a new message would be rejected by the server
const isBusy = computed(() => isStreaming.value || !!agentStore.pendingConfirmation);
const canSend = computed(() => !isBusy.value && input.value.trim().length > 0);

const onSend = () => {
  if (!canSend.value) return;
  agentStore.sendMessage(input.value.trim());
  input.value = '';
};

const onReset = () => {
  agentStore.reset();
  input.value = '';
};
</script>
